import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { track, identify, trackSignUp, EVENTS } from "@/lib/analytics";
import { toast } from "sonner";

/**
 * <MagicLinkConsume /> — the `/magic?token=…` landing page.
 *
 * A returning caregiver clicks the link in their inbox. We exchange the
 * one-shot token for a JWT, persist it, refresh the auth context, and
 * navigate them to /dashboard (or the redirect_to path the sender
 * requested). Fails cleanly if the token is expired or already used
 * so email-preview scanners don't spend the token.
 */
export default function MagicLinkConsume() {
    const [state, setState] = useState({ phase: "loading", message: "" });
    const [firstName, setFirstName] = useState("");
    const [saving, setSaving] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { refresh } = useAuth();

    const finishName = async (e) => {
        e?.preventDefault?.();
        const first = firstName.trim();
        if (first) {
            setSaving(true);
            try {
                await api.post("/me/name", { name: first });
                await refresh();
            } catch { /* non-blocking — already signed in */ }
            setSaving(false);
        }
        navigate("/dashboard", { replace: true });
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const token = params.get("token");
        // `plt` = passwordless login token from the code+link sign-in email
        // (single-use, 10-minute expiry). Distinct from the legacy/drip
        // `token` param so both link generations keep working.
        const plt = params.get("plt");
        if (!token && !plt) {
            setState({ phase: "err", message: "Missing sign-in token. Ask for a new link on the sign-in page." });
            return;
        }
        (async () => {
            try {
                if (plt) {
                    const r = await api.post("/auth/passwordless/verify-link", { token: plt });
                    setToken(r.data.token);
                    if (r.data.user?.primary_session_id) {
                        localStorage.setItem("mlcn_session_id", r.data.user.primary_session_id);
                    }
                    await refresh();
                    // Scrub the single-use token from the address bar BEFORE
                    // any analytics fire — the Reddit pixel auto-captures the
                    // full page URL with each event (privacy review, Jun 2026).
                    try { window.history.replaceState(null, "", "/magic"); } catch { /* noop */ }
                    if (r.data.new_account) {
                        trackSignUp(r.data.user, { method: "passwordless_link" });
                    } else {
                        identify(r.data.user?.id);
                        track(EVENTS.LOGIN, { method: "passwordless_link" });
                    }
                    setState({ phase: "ok", message: "" });
                    toast.success(r.data.new_account ? "Welcome to Memory Lane." : "Welcome back to Memory Lane.");
                    // Nameless new account → ask for a first name (skippable)
                    // so the greeting is never an email fragment.
                    if (r.data.new_account && !((r.data.user?.name || "").trim())) {
                        setState({ phase: "name", message: "" });
                        return;
                    }
                    setTimeout(() => navigate("/dashboard", { replace: true }), 400);
                    return;
                }
                const next = params.get("next") || "";
                const r = await api.get("/auth/magic-link/consume", { params: { token, ...(next ? { next } : {}) } });
                setToken(r.data.token);
                await refresh();
                setState({ phase: "ok", message: "" });
                toast.success("Welcome back to Memory Lane.");
                setTimeout(() => navigate(r.data.redirect_to || "/dashboard", { replace: true }), 400);
            } catch (e) {
                setState({
                    phase: "err",
                    message: e?.response?.data?.detail || "This sign-in link no longer works. Request a new one.",
                });
            }
        })();
    }, [location.search, navigate, refresh]);

    return (
        <div className="min-h-screen bg-cream-light flex items-center justify-center p-4" data-testid="magic-link-consume">
            <div className="ml-card p-8 max-w-md w-full text-center">
                {state.phase === "loading" && (
                    <>
                        <Loader2 className="animate-spin mx-auto mb-3 text-teal-dark" size={28} strokeWidth={1.8} />
                        <p className="text-[#4C5551]">Signing you in…</p>
                    </>
                )}
                {state.phase === "ok" && (
                    <>
                        <CheckCircle2 className="mx-auto mb-3 text-stage_early-dark" size={32} strokeWidth={1.8} />
                        <p className="text-[#1F2624] font-medium">Signed in. Taking you to your roadmap…</p>
                    </>
                )}
                {state.phase === "name" && (
                    <form onSubmit={finishName} noValidate data-testid="magic-link-name-form">
                        <CheckCircle2 className="mx-auto mb-3 text-stage_early-dark" size={32} strokeWidth={1.8} />
                        <p className="text-[#1F2624] font-medium">You&apos;re in. What should we call you?</p>
                        <p className="text-sm text-[#4C5551] mt-1 mb-4">A first name is plenty.</p>
                        <input
                            type="text"
                            data-testid="magic-link-name-input"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="Your first name"
                            className="ml-input w-full text-left"
                            autoComplete="given-name"
                            autoFocus
                        />
                        <button
                            type="submit"
                            disabled={saving}
                            data-testid="magic-link-name-continue"
                            className="ml-btn-primary w-full mt-3 disabled:opacity-50"
                        >
                            {saving ? "One moment…" : "Continue"}
                        </button>
                        <button
                            type="button"
                            disabled={saving}
                            data-testid="magic-link-name-skip"
                            onClick={() => navigate("/dashboard", { replace: true })}
                            className="w-full text-sm text-[#6E7A74] underline underline-offset-2 min-h-[44px] mt-1"
                        >
                            Skip for now
                        </button>
                    </form>
                )}
                {state.phase === "err" && (
                    <>
                        <XCircle className="mx-auto mb-3 text-coral-dark" size={32} strokeWidth={1.8} />
                        <p className="text-[#1F2624] font-medium mb-2">Sign-in link issue</p>
                        <p className="text-sm text-[#4C5551] mb-4">{state.message}</p>
                        <button
                            onClick={() => navigate("/login")}
                            className="ml-btn-primary"
                            data-testid="magic-link-back-to-login"
                        >
                            Request a new link
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
